<template>
  <div class="c-general-tab__section">
    <h3>Integration Status </h3>
    <div
      :class="[feedStatusTyped.statusBgColor, feedStatusTyped.textColor]"
      class="c-other-section__status"
    >
      {{ feedStatusTyped.text }}
    </div>
    <BittsAlert
      v-if="feedHasErrorMessage"
      :message="feed?.error_message || ''"
      color="error"
      class="mt-8 mx-24"
    />
  </div>
  <div v-if="!isGSheetFeed" class="c-general-tab__section">
    <h3>Sync Data from {{ feed?.integration?.friendly_name }}</h3>
    <BittsSwitch
      v-model="isSyncing"
      :disabled="!canManageDataSources"
      data-testid="c-datasource-settings-source-toggle"
    />
  </div>
  <div
    v-if="!isGSheetFeed && !isDellSpilFeed && !isExternalFileDataFeed"
    class="c-general-tab__section"
  >
    <h3>Update Frequency</h3>
    <BittsSelect
      v-if="canManageDataSources"
      aria-label="Sync frequency"
      v-model="currentFrequency"
      :options="FREQUENCIES"
      :disabled="feed?.integration?.type === SNOWFLAKE_DATA_SOURCE_TYPE"
      :dropdown-match-select-width="false"
      :searchable="false"
      class="c-other-section-frequency-selector"
      size="medium"
    />
    <div v-else>{{ frequencyFriendlyName }}</div>
  </div>
  <FieldMappingSection v-if="!isSnowflakeFeed" />
  <div
    v-if="!isGSheetFeed && !isDellSpilFeed && !isExternalFileDataFeed"
    class="c-general-tab__section"
  >
    <div class="c-general-tab__section-with-description">
      <div>
        <h3 class="mb-4">Connection Details</h3>
        <BittsTag v-if="feed?.is_sandbox" class="mb-8"> Sandbox </BittsTag>
        <div class="c-general-tab__section-with-description__text">
          <span>
            <span v-if="feedConnectedAt">
              {{ feedConnectedAt }}
            </span>
            <span v-if="feedConnectedAt && connectorName">
              by {{ connectorName }}
            </span>
            <span v-if="feedConnectedAt && authorizingUser">
              ({{ authorizingUser }})
            </span>
          </span>
          <span
            v-if="feed?.custom_base_url"
            data-testid="c-datasource-settings-proxy-url"
          >
            Through proxy {{ feed.custom_base_url }}
          </span>
        </div>
      </div>
      <BittsButton
        v-if="!hideReauthorizeButton"
        :disabled="!canManageDataSources"
        text="Reauthorize"
        variant="outline"
        size="small"
        data-testid="c-datasource-settings-reauthorize"
        @click="handleReauthorize"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsButton,
  BittsSelect,
  BittsSwitch,
  BittsTag,
} from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { ComputedRef, computed } from 'vue';
import { useRoute } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useDataSourceSettings from '@/composables/useDataSourceSettings';
import useFeedDetails from '@/composables/useFeedDetails';
import { SNOWFLAKE_DATA_SOURCE_TYPE } from '@/constants/data_sources';
import { useTeamStore } from '@/stores';

import FieldMappingSection from './FieldMappingSection.vue';

const route = useRoute();
const teamStore = useTeamStore();

const {
  feed,
  currentFrequency,
  isSyncing,
  isGSheetFeed,
  isSnowflakeFeed,
  isDatabricksFeed,
  isDellSpilFeed,
  isExternalFileDataFeed,
  isAwsPartnerCentralFeed,
  handleReauthorize,
} = useDataSourceSettings();

const feedId = Number(route.params.id);

const { hasPermission } = useAuth();
const canManageDataSources = computed(() =>
  hasPermission('manage:data-sources'),
);

const { feedStatus, feedHasErrorMessage } = useFeedDetails({ feedId });
const feedStatusTyped = feedStatus as ComputedRef<{
  statusBgColor: string;
  textColor: string;
  text: string;
}>;

/* Change frequency */
const FREQUENCIES = [
  { label: '6 Hours', value: 360 },
  { label: '12 Hours', value: 720 },
  { label: '24 Hours', value: 1440 },
  { label: '1 Week', value: 10080 },
];

const frequencyFriendlyName = computed(
  () =>
    FREQUENCIES.find((freq) => freq.value === currentFrequency.value)?.label,
);

const feedConnectedAt = computed(() => {
  if (feed.value?.connected_at) {
    const time = DateTime.fromISO(feed.value?.connected_at);
    return `Connected on ${time.toFormat('MMMM d, yyyy')} at ${time.toFormat('h:m a')}`;
  }
  return null;
});

const connectorName = computed(() => {
  const connector = teamStore.authorizations.filter(
    (authorization) => authorization.user.email === feed.value?.connected_by,
  )[0];
  return connector
    ? `${connector.user.first_name} ${connector.user.last_name}`
    : feed.value?.connected_by;
});

const authorizingUser = computed(() => feed.value?.external_user_email);

const hideReauthorizeButton = computed(
  () =>
    isDatabricksFeed.value ||
    isAwsPartnerCentralFeed.value ||
    (isSnowflakeFeed.value && !feed.value?.integration.has_auth_step),
);
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.c-other-section__status {
  @apply text-sm rounded-3xl py-2 px-8 flex items-center gap-4;
}

.c-other-section-frequency-selector {
  width: 200px;
}
</style>
